import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useDataUpdates } from '../hooks/useDataUpdates';
import '../styles/Indexers.css';
import { getApiBase } from '../utils/apiBase';
import { useStatus } from '../context/StatusContext';
import { StatusEvaluator } from '../context/StatusEvaluator';
import LoadingAnimation from '../components/loadingAnimation';
import { useDataCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';

function IndexerStates() {
  const [indexerData, setIndexerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { updatePageStatus } = useStatus();
  const scrollerRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [numSlides, setNumSlides] = useState(0);
  const { getCachedData, setCachedData } = useDataCache();

  const isInitialLoadRef = useRef(true);

  const handleDataUpdate = useCallback(async () => {
    if (isInitialLoadRef.current) {
      setLoading(true);
      // Attempt instant hydration from cache
      try {
        const cached = getCachedData('/indexer_states.json');
        if (cached && typeof cached === 'object') {
          setIndexerData(cached);
          const st = StatusEvaluator.indexers(cached);
          updatePageStatus('indexers', st);
          setLoading(false);
        }
      } catch {}
    }
    setError(null);
    try {
      const API = getApiBase();
      const response = await fetch(`${API}/indexer_states.json`);

      const data = await response.json();
      setIndexerData(data);
      setCachedData('/indexer_states.json', data);
      const status = StatusEvaluator.indexers(data);
      updatePageStatus('indexers', status);
    } catch (error) {
      console.error('Error fetching indexer states:', error);
      setError(error.message);
      // Do not force overwrite with red if we had a cached state already
      if (!indexerData) updatePageStatus('indexers', 'red');
    }
    finally {
      if (isInitialLoadRef.current) setLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [updatePageStatus, getCachedData, setCachedData, indexerData]);

  // Initial data fetch
  useEffect(() => {
    try { cacheManager.registerEndpoints('indexers', ['/indexer_states.json']); } catch {}
    handleDataUpdate();
    return () => { try { cacheManager.unregisterEndpoints('indexers'); } catch {} };
  }, [handleDataUpdate]);

  // Setup SSE updates
  useDataUpdates(handleDataUpdate);

  // Calculate numSlides when indexerData changes
  useEffect(() => {
    if (indexerData) {
      let count = 0;
      Object.entries(indexerData).forEach(([entity, networks]) => {
        if (entity === 'ERRORS') return;
        if (networks && typeof networks === 'object') {
          Object.values(networks).forEach((environments) => {
            if (environments && typeof environments === 'object') {
              count += Object.keys(environments).length;
            }
          });
        }
      });
      setNumSlides(count);
    }
  }, [indexerData]);

  // Scroll handler for pagination dots
  const handleScroll = useCallback(() => {
    if (scrollerRef.current && numSlides > 0) {
      const scrollLeft = scrollerRef.current.scrollLeft;
      const cardWidth = scrollerRef.current.firstChild ? scrollerRef.current.firstChild.offsetWidth : 0;
      const gap = 20;
      const slideWidth = cardWidth + gap;
      
      if (slideWidth > 0) {
        // Calculate current slide with a small tolerance towards the start
        const currentSlide = Math.round((scrollLeft + (slideWidth * 0.1)) / slideWidth);
        setActiveSlide(Math.max(0, Math.min(currentSlide, numSlides - 1)));
      }
    }
  }, [numSlides]);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller && numSlides > 0) {
      scroller.scrollLeft = 0;
      handleScroll(); 
    }
  }, [handleScroll, numSlides]);
  
  if (loading) {
    return <LoadingAnimation />;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }
  
  if (!indexerData || Object.keys(indexerData).length === 0) {
    return <div className="loading">No indexer data available.</div>;
  }

  const formatTimestamp = (timestamp, isISOString = false) => {
    const date = isISOString ? new Date(timestamp) : new Date(timestamp * 1000);
    return date.toLocaleString('en-GB', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getHeaderStatus = (blockDifference) => {
    if (blockDifference === null || blockDifference === undefined) return 'bg-red';
    if (blockDifference <= 4) return 'bg-green';
    if (blockDifference <= 10) return 'bg-amber';
    return 'bg-red';
  };
  
  const getHeaderIcon = (blockDifference) => {
    if (blockDifference === null || blockDifference === undefined) return "https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671760d7912d60d1ebb42595_info-circle%20(1).svg";
    if (blockDifference <= 4) return "https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671760d711a4e1bbe2b0489d_tick-circle%20(1).svg";
    if (blockDifference <= 10) return "https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671760d7912d60d1ebb42595_info-circle%20(1).svg";
    return "https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671760d7912d60d1ebb42595_info-circle%20(1).svg";
  };

  const formatName = (name) => {
    return name.replace(/_/g, ' ');
  };

  const formatIndexerIp = (ipAddress) => {
    if (!ipAddress) return null;
    return ipAddress;
  };

  /** Scaling / generic backup indexers use connection key prefix `generic_*` → JSON entity `GENERIC`; omit that label in the UI. */
  const formatIndexerCardTitle = (entity, network, env) => {
    const net = network.replace(/_/g, ' ');
    const e = env.replace(/_/g, ' ');
    if (String(entity).toUpperCase() === 'GENERIC') {
      return `${net} ${e}`.trim();
    }
    return `${entity} ${net} ${e}`.trim();
  };

  return (
    <div className="indexer-states-container">
      <div className="indexer-boxes-wrapper" ref={scrollerRef} onScroll={handleScroll}>
        {Object.entries(indexerData).map(([entity, networks]) => (
          entity === 'ERRORS' ? null : (
          (networks && typeof networks === 'object') ? Object.entries(networks).map(([network, environments]) => (
            (environments && typeof environments === 'object') ? Object.entries(environments).map(([env, envDataRaw]) => {
              const envData = (envDataRaw && typeof envDataRaw === 'object') ? envDataRaw : { data: [], block_difference: null };
              return (
              <div key={`${entity}-${network}-${env}`} className="indexer-box">
                <h2 className={`indexer-header ${getHeaderStatus(envData?.block_difference)}`}>
                  <div className="header-left">
                    <span className="header-title">
                      {formatIndexerCardTitle(entity, network, env)}
                    </span>
                    {formatIndexerIp(envData?.ip_address) && (
                      <span className="header-subtitle">{formatIndexerIp(envData?.ip_address)}</span>
                    )}
                  </div>
                  <div className="header-right">
                    <span className="block-difference">
                      ({envData?.block_difference !== null && envData?.block_difference !== undefined ? envData.block_difference : 'N/A'})
                    </span>
                    <img 
                      src={getHeaderIcon(envData?.block_difference)}
                      alt="Status Icon" 
                      className="header-icon"
                    />
                  </div>
                </h2>
                <div className="indexer-content">
                  {Array.isArray(envData?.data) && envData.data.map((item, index) => (
                    <div key={index} className="indexer-item">
                      <div className="item-name">{formatName(item.name)}</div>
                      <div className="item-details">
                        <div className="detail">
                          <span className="label">Block:</span>
                          <span className="value">{item.index}</span>
                        </div>
                        <div className="detail">
                            <span className="label">Timestamp:</span>
                            <span className="value">{formatTimestamp(item.block_timestamp)}</span>
                            </div>
                            <div className="detail">
                            <span className="label">Updated:</span>
                            <span className="value">{formatTimestamp(item.updated, true)}</span>
                            </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            }) : null
          )) : null)
        ))}
      </div>
      {numSlides > 6 && (
        <div className="pagination-dots-container mobile-only-pagination">
          {Array.from({ length: numSlides }).map((_, index) => (
            <div
              key={index}
              className={`dot ${index === activeSlide ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default IndexerStates;